import React, { useState, useEffect, useCallback } from "react";
import api, { errMsg } from "../api";
import { useAuth } from "../AuthContext";
import { t, LOCALES } from "../i18n";
import VoiceFillButton from "../components/VoiceFillButton";
import DropZone from "../components/DropZone";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "../components/ui/dialog";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import { Separator } from "../components/ui/separator";
import { useSearchParams } from "react-router-dom";
import {
    Plus, Wallet, CheckCircle2, Clock, ListChecks, Hash, AlertTriangle, X as XIcon,
    CheckSquare, FileText, CreditCard, Paperclip, Download, Loader2, Trash2, Send,
    Calendar as CalendarIcon, BarChart3, Save, ChevronLeft, ChevronRight, Edit3
} from "lucide-react";
import { toast } from "sonner";

const CUR_SYM = { TRY: "₺", EUR: "€", USD: "$" };

const COLUMNS = [
    { key: "open", icon: ListChecks, accent: "bg-slate-100 text-slate-700 border-slate-200" },
    { key: "approved", icon: CheckSquare, accent: "bg-blue-100 text-blue-800 border-blue-200" },
    { key: "in_progress", icon: Clock, accent: "bg-amber-100 text-amber-800 border-amber-200" },
    { key: "paid", icon: CheckCircle2, accent: "bg-emerald-100 text-emerald-800 border-emerald-200" },
];

const CATS = ["rent", "salary", "utility", "vendor", "tax", "other"];

function fmtMoney(amount, cur) {
    const sym = CUR_SYM[cur] || "";
    const n = Number(amount || 0);
    return `${sym}${n.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function BankingPage() {
    const { user, lang } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const [invoices, setInvoices] = useState([]);
    const [stats, setStats] = useState({ open: 0, approved: 0, in_progress: 0, paid: 0, total: 0, sum_total: 0, sum_open: 0, sum_paid: 0 });
    const [users, setUsers] = useState([]);
    const [spaces, setSpaces] = useState([]);
    const [newOpen, setNewOpen] = useState(false);
    const [openId, setOpenId] = useState(null);
    const [dateFilter, setDateFilter] = useState("all");
    const [chartOpen, setChartOpen] = useState(false);

    const invParam = searchParams.get("invoice");
    const isPriv = user?.role === "admin" || user?.role === "master";

    const load = useCallback(async () => {
        const [inv, st, u, sp] = await Promise.all([
            api.get("/invoices"),
            api.get("/invoices/stats"),
            api.get("/users").catch(() => ({ data: [] })),
            api.get("/spaces").catch(() => ({ data: [] })),
        ]);
        setInvoices(inv.data);
        setStats(st.data);
        setUsers(u.data);
        setSpaces(sp.data);
    }, []);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (invParam) setOpenId(invParam);
    }, [invParam]);

    const filtered = React.useMemo(() => {
        if (dateFilter === "all") return invoices;
        const now = new Date();
        const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const endOfDay = new Date(startOfDay); endOfDay.setDate(endOfDay.getDate() + 1);
        const dow = (startOfDay.getDay() + 6) % 7; // Mon=0
        const startOfWeek = new Date(startOfDay); startOfWeek.setDate(startOfDay.getDate() - dow);
        const endOfWeek = new Date(startOfWeek); endOfWeek.setDate(startOfWeek.getDate() + 7);
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        return invoices.filter((iv) => {
            if (!iv.due_date) return false;
            const d = new Date(iv.due_date);
            if (dateFilter === "today") return d >= startOfDay && d < endOfDay;
            if (dateFilter === "week") return d >= startOfWeek && d < endOfWeek;
            if (dateFilter === "month") return d >= startOfMonth && d < endOfMonth;
            return true;
        });
    }, [invoices, dateFilter]);

    const filteredSums = React.useMemo(() => {
        const open = filtered.filter((iv) => iv.status !== "paid").reduce((s, iv) => s + Number(iv.amount || 0), 0);
        const paid = filtered.filter((iv) => iv.status === "paid").reduce((s, iv) => s + Number(iv.amount || 0), 0);
        return { open, paid, total: open + paid };
    }, [filtered]);

    const FILTER_LABELS = {
        all: { de: "Alle", en: "All", tr: "Tümü" },
        today: { de: "Heute", en: "Today", tr: "Bugün" },
        week: { de: "Diese Woche", en: "This Week", tr: "Bu Hafta" },
        month: { de: "Diesen Monat", en: "This Month", tr: "Bu Ay" },
    };
    const useFiltered = dateFilter !== "all";
    const displayedSums = useFiltered ? filteredSums : { open: stats.sum_open, paid: stats.sum_paid, total: stats.sum_total };
    const displayedInvoices = filtered;
    const handleDragOver = (e) => e.preventDefault();
    const handleDrop = async (e, status) => {
        e.preventDefault();
        const id = e.dataTransfer.getData("inv-id");
        if (!id) return;
        try {
            await api.patch(`/invoices/${id}`, { status });
            load();
        } catch (err) {
            toast.error(errMsg(err, "Error"));
        }
    };

    return (
        <div className="p-4 sm:p-6 md:p-8 space-y-6" data-testid="banking-page">
            {/* Header */}
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500 mb-1 flex items-center gap-1.5">
                        <Wallet className="w-3 h-3" /> {t(lang, "banking")}
                    </p>
                    <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">
                        {isPriv ? (lang === "tr" ? "Tüm Faturalar" : lang === "de" ? "Alle Rechnungen" : "All Invoices") : t(lang, "invoices")}
                    </h1>
                    <p className="text-xs text-neutral-500 mt-1">
                        {isPriv
                            ? (lang === "tr" ? "Admin/Master tüm girişleri görür" : lang === "de" ? "Admin/Master sehen alle Einträge" : "Admin/Master see all entries")
                            : (lang === "tr" ? "Sadece kullanıcı kayıtları görünür" : lang === "de" ? "Nur Benutzer-Einträge sichtbar" : "Only user entries visible")}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button onClick={() => setChartOpen(true)} variant="outline" data-testid="open-monthly-chart-btn" className="border-neutral-300">
                        <BarChart3 className="w-4 h-4 mr-1" /> {t(lang, "monthlyChart")}
                    </Button>
                    <Button onClick={() => setNewOpen(true)} className="bg-neutral-900 hover:bg-neutral-800" data-testid="new-invoice-btn">
                        <Plus className="w-4 h-4 mr-1" /> {t(lang, "newInvoice")}
                    </Button>
                </div>
            </div>

            {/* Quick Date Filter */}
            <div className="flex flex-wrap items-center gap-2" data-testid="date-filter-row">
                <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-neutral-500">
                    {lang === "de" ? "Zeitraum" : lang === "tr" ? "Dönem" : "Period"}:
                </span>
                {["all", "today", "week", "month"].map((k) => (
                    <button
                        key={k}
                        onClick={() => setDateFilter(k)}
                        data-testid={`date-filter-${k}`}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium border transition ${
                            dateFilter === k
                                ? "bg-neutral-900 text-white border-neutral-900"
                                : "border-neutral-300 text-neutral-700 hover:bg-neutral-50"
                        }`}
                    >
                        {FILTER_LABELS[k][lang] || FILTER_LABELS[k].en}
                    </button>
                ))}
                {useFiltered && (
                    <span className="text-xs text-neutral-500 ml-1">
                        ({displayedInvoices.length} {lang === "de" ? "Einträge" : lang === "tr" ? "kayıt" : "items"})
                    </span>
                )}
            </div>

            {/* Total Expenses Overview */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="banking-totals">
                <div className="rounded-lg border border-neutral-900 bg-neutral-900 text-white p-4" data-testid="total-all">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-neutral-300 mb-1">
                        {lang === "de" ? "Gesamtausgaben" : lang === "tr" ? "Toplam Tutar" : "Total Expenses"}
                    </p>
                    <p className="font-display text-3xl font-bold tracking-tight font-mono">{fmtMoney(displayedSums.total, "EUR")}</p>
                </div>
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="total-open">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-amber-700 mb-1">
                        {lang === "de" ? "Offene Forderungen" : lang === "tr" ? "Açık" : "Open"}
                    </p>
                    <p className="font-display text-3xl font-bold tracking-tight font-mono text-amber-900">{fmtMoney(displayedSums.open, "EUR")}</p>
                </div>
                <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-4" data-testid="total-paid">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-emerald-700 mb-1">
                        {lang === "de" ? "Bezahlt" : lang === "tr" ? "Ödendi" : "Paid"}
                    </p>
                    <p className="font-display text-3xl font-bold tracking-tight font-mono text-emerald-900">{fmtMoney(displayedSums.paid, "EUR")}</p>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="banking-stats">
                <StatCard label={t(lang, "total")} value={stats.total} icon={Hash} highlight />
                <StatCard label={t(lang, "open")} value={stats.open} icon={ListChecks} />
                <StatCard label={t(lang, "approved")} value={stats.approved} icon={CheckSquare} />
                <StatCard label={t(lang, "inProgress")} value={stats.in_progress} icon={Clock} />
                <StatCard label={t(lang, "paid")} value={stats.paid} icon={CheckCircle2} green />
            </div>

            {/* Kanban */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="banking-kanban">
                {COLUMNS.map((col) => {
                    const colItems = displayedInvoices.filter((iv) => iv.status === col.key);
                    return (
                        <div
                            key={col.key}
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, col.key)}
                            data-testid={`banking-col-${col.key}`}
                            className={`flex flex-col gap-2.5 rounded-lg border p-3 min-h-[300px] ${col.key === "paid" ? "border-emerald-300 bg-emerald-50/40" : "border-neutral-200 bg-neutral-50/70"}`}
                        >
                            <div className="flex items-center justify-between mb-1">
                                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-md border ${col.accent}`}>
                                    {t(lang, col.key === "in_progress" ? "inProgress" : col.key)}
                                </span>
                                <span className="text-xs text-neutral-500">{colItems.length}</span>
                            </div>
                            <div className="space-y-2">
                                {colItems.map((iv) => (
                                    <InvoiceCard key={iv.id} inv={iv} users={users} onOpen={() => setOpenId(iv.id)}
                                        lang={lang}
                                        draggable={isPriv}
                                        onDragStart={(e) => e.dataTransfer.setData("inv-id", iv.id)} />
                                ))}
                                {colItems.length === 0 && <p className="text-xs text-neutral-400 text-center py-6">—</p>}
                            </div>
                        </div>
                    );
                })}
            </div>

            <NewInvoiceDialog open={newOpen} onClose={() => setNewOpen(false)} onCreated={load} users={users} spaces={spaces} lang={lang} />
            <InvoiceDetailDialog
                invId={openId}
                open={!!openId}
                onClose={() => { setOpenId(null); setSearchParams((p) => { const x = new URLSearchParams(p); x.delete("invoice"); return x; }); }}
                onChange={load}
                users={users}
                spaces={spaces}
                lang={lang}
                user={user}
            />
            <MonthlyChartDialog open={chartOpen} onClose={() => setChartOpen(false)} lang={lang} />
        </div>
    );
}

function StatCard({ label, value, icon: Icon, highlight, green }) {
    const cls = green
        ? "border-emerald-500 bg-emerald-50 text-emerald-900"
        : highlight ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-200 bg-white";
    const lblCls = green ? "text-emerald-700" : highlight ? "text-neutral-300" : "text-neutral-500";
    const iconCls = green ? "text-emerald-700" : highlight ? "text-white" : "text-neutral-400";
    return (
        <div className={`rounded-lg border p-3 sm:p-4 ${cls}`} data-testid={`stat-${label}`}>
            <div className="flex items-center justify-between mb-2">
                <span className={`text-[9px] sm:text-[10px] font-semibold uppercase tracking-[0.15em] ${lblCls} truncate`}>{label}</span>
                <Icon className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${iconCls}`} />
            </div>
            <p className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{value}</p>
        </div>
    );
}

function InvoiceCard({ inv, users, onOpen, lang, draggable, onDragStart }) {
    const overdue = inv.due_date && new Date(inv.due_date) < new Date() && inv.status !== "paid";
    const isPaid = inv.status === "paid";
    const urg = {
        high: "bg-red-50 text-red-700 border-red-200",
        medium: "bg-orange-50 text-orange-700 border-orange-200",
        low: "bg-stone-50 text-stone-700 border-stone-200",
    }[inv.urgency] || "";
    const assignees = (inv.assignee_ids || []).map((id) => users.find((u) => u.id === id)).filter(Boolean);
    return (
        <div onClick={onOpen} draggable={draggable} onDragStart={onDragStart}
            data-testid={`invoice-card-${inv.id}`}
            className={`group flex flex-col gap-2 rounded-md border-2 bg-white p-3 shadow-sm hover:shadow-md transition-all cursor-pointer ${
                overdue ? "border-red-500 bg-red-50/50 hover:border-red-600 animate-pulse-slow" :
                isPaid ? "border-emerald-300 bg-emerald-50/30" :
                "border-neutral-200 hover:border-neutral-300"
            }`}>
            <div className="flex items-start justify-between gap-2">
                <h3 className={`text-sm font-semibold leading-snug flex-1 line-clamp-2 flex items-center gap-1 ${
                    overdue ? "text-red-900" :
                    isPaid ? "line-through text-emerald-800" :
                    "text-neutral-900"
                }`}>
                    {overdue && <AlertTriangle className="w-3.5 h-3.5 text-red-600 shrink-0" />}
                    {inv.title}
                </h3>
                <Badge variant="outline" className={`shrink-0 text-[10px] uppercase tracking-wider px-1.5 py-0 h-5 ${overdue ? "bg-red-100 text-red-800 border-red-300 font-bold" : urg}`}>
                    {overdue ? (lang === "de" ? "ÜBERFÄLLIG" : lang === "tr" ? "GECİKMİŞ" : "OVERDUE") : inv.urgency}
                </Badge>
            </div>
            <div className="flex items-center gap-2">
                <span className="font-mono font-bold text-base text-neutral-900">{fmtMoney(inv.amount, inv.currency)}</span>
                {inv.requires_approval && inv.status === "open" && (
                    <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-[9px] uppercase">
                        <AlertTriangle className="w-2.5 h-2.5 mr-1" />onay bekliyor
                    </Badge>
                )}
            </div>
            {inv.vendor_name && <p className="text-[11px] text-neutral-500 truncate">{inv.vendor_name}</p>}
            <div className="flex items-center justify-between pt-1 border-t border-neutral-100">
                {inv.due_date && (
                    <span className={`text-[10px] ${overdue ? "text-red-700 font-bold" : "text-neutral-500"}`}>
                        {overdue && "⚠ "}{new Date(inv.due_date).toLocaleDateString(LOCALES[lang])}
                    </span>
                )}
                {assignees.length > 0 && (
                    <div className="flex -space-x-1.5">
                        {assignees.slice(0, 3).map((u) => (
                            <div key={u.id} title={u.name} className="w-5 h-5 rounded-full bg-neutral-900 text-white text-[9px] font-semibold flex items-center justify-center border-2 border-white">
                                {u.name?.slice(0, 2)?.toUpperCase()}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function NewInvoiceDialog({ open, onClose, onCreated, users, spaces, lang }) {
    const [title, setTitle] = useState("");
    const [amount, setAmount] = useState("");
    const [dueDate, setDueDate] = useState("");
    const [urgency, setUrgency] = useState("medium");
    const [vendor, setVendor] = useState("");
    const [iban, setIban] = useState("");
    const [invNo, setInvNo] = useState("");
    const [category, setCategory] = useState("other");
    const [recurring, setRecurring] = useState("none");
    const [requiresApproval, setRequiresApproval] = useState(false);
    const [description, setDescription] = useState("");
    const [assignees, setAssignees] = useState([]);
    const [spaceIds, setSpaceIds] = useState([]);
    const [files, setFiles] = useState([]);
    const [busy, setBusy] = useState(false);
    const [progress, setProgress] = useState(0);
    const fileInputRef = React.useRef(null);

    useEffect(() => {
        if (open) {
            setTitle(""); setAmount(""); setDueDate(""); setUrgency("medium");
            setVendor(""); setIban(""); setInvNo(""); setCategory("other"); setRecurring("none");
            setRequiresApproval(false); setDescription(""); setAssignees([]); setSpaceIds([]); setFiles([]); setProgress(0);
        }
    }, [open]);

    const addFiles = (e) => {
        const list = Array.from(e.target.files || []);
        setFiles((prev) => [...prev, ...list]);
        e.target.value = "";
    };
    const addFileObject = (file) => {
        if (file) setFiles((prev) => [...prev, file]);
    };
    const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

    const submit = async () => {
        if (!title.trim() || !amount) return;
        setBusy(true);
        try {
            const { data: inv } = await api.post("/invoices", {
                title, amount: parseFloat(amount), currency: "EUR",
                due_date: dueDate ? new Date(dueDate).toISOString() : null,
                urgency, vendor_name: vendor, iban, invoice_number: invNo,
                category, recurring: recurring === "none" ? null : recurring,
                requires_approval: requiresApproval, description, assignee_ids: assignees, space_ids: spaceIds,
            });
            if (files.length > 0) {
                let done = 0;
                for (const f of files) {
                    const form = new FormData();
                    form.append("file", f);
                    try {
                        await api.post(`/invoices/${inv.id}/files`, form, {
                            headers: { "Content-Type": "multipart/form-data" },
                            onUploadProgress: (evt) => {
                                if (evt.total) {
                                    const single = (evt.loaded / evt.total) * 100;
                                    setProgress(Math.round(((done + single / 100) / files.length) * 100));
                                }
                            },
                        });
                    } catch (err) { toast.error(`${f.name}: ${errMsg(err, "upload failed")}`); }
                    done++;
                    setProgress(Math.round((done / files.length) * 100));
                }
            }
            toast.success(t(lang, "create"));
            onCreated(); onClose();
        } catch (e) {
            toast.error(errMsg(e, "Error"));
        } finally { setBusy(false); setProgress(0); }
    };

    const RECURRING_OPTS = [
        { value: "none", label: { de: "—", en: "—", tr: "—" } },
        { value: "daily", label: { de: "Täglich", en: "Daily", tr: "Günlük" } },
        { value: "weekly", label: { de: "Wöchentlich", en: "Weekly", tr: "Haftalık" } },
        { value: "start_month", label: { de: "Monatsanfang", en: "Start of month", tr: "Ay başı" } },
        { value: "mid_month", label: { de: "Mitte des Monats", en: "Mid month", tr: "Ay ortası" } },
        { value: "end_month", label: { de: "Monatsende", en: "End of month", tr: "Ay sonu" } },
        { value: "monthly", label: { de: "Monatlich", en: "Monthly", tr: "Aylık" } },
        { value: "yearly", label: { de: "Jährlich", en: "Yearly", tr: "Yıllık" } },
    ];

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-invoice-dialog">
                <DialogHeader>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                        <DialogTitle className="font-display text-2xl tracking-tight">{t(lang, "newInvoice")}</DialogTitle>
                        <VoiceFillButton
                            formType="invoice"
                            lang={lang}
                            onParsed={(f) => {
                                if (f.title) setTitle(String(f.title));
                                if (f.recipient) setVendor(String(f.recipient));
                                if (f.amount != null && !Number.isNaN(Number(f.amount))) setAmount(String(f.amount));
                                if (f.due_date) {
                                    // Add a default 17:00 time if only a date was parsed
                                    const d = String(f.due_date);
                                    setDueDate(d.length === 10 ? `${d}T17:00` : d);
                                }
                                if (f.iban) setIban(String(f.iban));
                                if (f.reference) setInvNo(String(f.reference));
                                if (f.category) setCategory(String(f.category).toLowerCase());
                            }}
                        />
                    </div>
                    <DialogDescription className="text-xs text-neutral-500">
                        {lang === "tr" ? "Banking sistemine yeni bir fatura/ödeme ekleyin" : lang === "de" ? "Neue Rechnung/Zahlung im Banking-System eintragen" : "Add a new invoice/payment"}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "title")}</Label>
                        <Input data-testid="inv-title" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1.5" />
                    </div>
                    <div className="grid grid-cols-[1fr,80px] gap-3">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "amount")}</Label>
                            <Input data-testid="inv-amount" type="number" inputMode="decimal" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5" placeholder="0.00" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "currency")}</Label>
                            <div className="mt-1.5 h-10 rounded-md border border-neutral-200 bg-neutral-50 px-3 flex items-center font-medium text-sm" data-testid="inv-currency">€ EUR</div>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "dueDate")}</Label>
                            <Input type="datetime-local" data-testid="inv-due" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="mt-1.5" />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "urgency")}</Label>
                            <Select value={urgency} onValueChange={setUrgency}>
                                <SelectTrigger data-testid="inv-urgency" className="mt-1.5"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="low">{t(lang, "low")}</SelectItem>
                                    <SelectItem value="medium">{t(lang, "medium")}</SelectItem>
                                    <SelectItem value="high">{t(lang, "high")}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "vendor")}</Label>
                        <Input data-testid="inv-vendor" value={vendor} onChange={(e) => setVendor(e.target.value)} className="mt-1.5" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "iban")}</Label>
                            <Input data-testid="inv-iban" value={iban} onChange={(e) => setIban(e.target.value)} className="mt-1.5 font-mono text-xs" placeholder="DE00..." />
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "invoiceNumber")}</Label>
                            <Input data-testid="inv-no" value={invNo} onChange={(e) => setInvNo(e.target.value)} className="mt-1.5" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "category")}</Label>
                            <Select value={category} onValueChange={setCategory}>
                                <SelectTrigger data-testid="inv-cat" className="mt-1.5"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "recurring")}</Label>
                            <Select value={recurring} onValueChange={setRecurring}>
                                <SelectTrigger data-testid="inv-rec" className="mt-1.5"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {RECURRING_OPTS.map((o) => (
                                        <SelectItem key={o.value} value={o.value} data-testid={`inv-rec-${o.value}`}>{o.label[lang] || o.label.en}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "description")}</Label>
                        <Textarea data-testid="inv-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="mt-1.5" />
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{t(lang, "assignees")}</Label>
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                            {users.map((u) => {
                                const active = assignees.includes(u.id);
                                return (
                                    <button key={u.id} type="button" data-testid={`inv-asg-${u.id}`}
                                        onClick={() => setAssignees((p) => p.includes(u.id) ? p.filter((x) => x !== u.id) : [...p, u.id])}
                                        className={`px-2 py-1 rounded-md text-xs border ${active ? "bg-neutral-900 text-white border-neutral-900" : "border-neutral-300 text-neutral-700 hover:bg-neutral-100"}`}>
                                        {u.name}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    {(spaces || []).length > 0 && (
                        <div>
                            <Label className="text-xs font-semibold uppercase tracking-wider">{lang === "de" ? "Abteilungen" : "Departments"}</Label>
                            <div className="flex flex-wrap gap-1.5 mt-1.5">
                                {spaces.map((s) => {
                                    const active = spaceIds.includes(s.id);
                                    return (
                                        <button key={s.id} type="button" data-testid={`inv-space-${s.id}`}
                                            onClick={() => setSpaceIds((p) => p.includes(s.id) ? p.filter((x) => x !== s.id) : [...p, s.id])}
                                            className={`px-2 py-1 rounded-md text-xs border ${active ? "bg-neutral-900 text-white border-neutral-900" : "border-neutral-300 text-neutral-700 hover:bg-neutral-100"}`}
                                            style={active ? {} : { borderLeft: `3px solid ${s.color || "#0a0a0a"}` }}>
                                            {s.name}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* PDF / File attachments */}
                    <div>
                        <div className="flex items-center justify-between">
                            <Label className="text-xs font-semibold uppercase tracking-wider">
                                {t(lang, "attachments")} {files.length > 0 && <span className="font-normal text-neutral-500">({files.length})</span>}
                            </Label>
                            <button type="button" onClick={() => fileInputRef.current?.click()} data-testid="inv-add-files"
                                className="text-xs flex items-center gap-1 px-2 py-1 rounded-md border border-neutral-300 hover:bg-neutral-50">
                                <Paperclip className="w-3 h-3" />PDF / {t(lang, "attachFiles")}
                            </button>
                            <input ref={fileInputRef} type="file" hidden multiple accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx" onChange={addFiles} data-testid="inv-file-input" />
                        </div>
                        <DropZone
                            accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx"
                            multiple
                            onFile={addFileObject}
                            compact
                            label={lang === "de" ? "Dateien hier ablegen, Str+V einfügen oder klicken" : "Drop files here, paste with Ctrl+V, or click"}
                            testId="inv-dropzone"
                            className="mt-1.5"
                        />
                        {files.length > 0 && (
                            <div className="mt-1.5 space-y-1">
                                {files.map((f, i) => (
                                    <div key={i} className="flex items-center gap-2 p-1.5 rounded-md border border-neutral-200 bg-neutral-50/60 text-xs" data-testid={`inv-file-${i}`}>
                                        <FileText className="w-3 h-3 text-neutral-500" />
                                        <span className="flex-1 truncate">{f.name}</span>
                                        <span className="text-neutral-500 text-[10px]">{(f.size / 1024).toFixed(1)} KB</span>
                                        <button onClick={() => removeFile(i)} className="text-neutral-500 hover:text-red-600"><XIcon className="w-3 h-3" /></button>
                                    </div>
                                ))}
                            </div>
                        )}
                        {busy && files.length > 0 && (
                            <div className="mt-2 h-1 bg-neutral-100 rounded overflow-hidden">
                                <div className="h-full bg-neutral-900 transition-all" style={{ width: `${progress}%` }} />
                            </div>
                        )}
                    </div>

                    <label className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="checkbox" data-testid="inv-approval" checked={requiresApproval} onChange={(e) => setRequiresApproval(e.target.checked)} className="w-4 h-4 accent-neutral-900" />
                        {t(lang, "requiresApproval")}
                    </label>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy}>{t(lang, "cancel")}</Button>
                    <Button onClick={submit} disabled={busy || !title.trim() || !amount} data-testid="inv-create" className="bg-neutral-900 hover:bg-neutral-800">
                        {busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
                        {t(lang, "create")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function InvoiceDetailDialog({ invId, open, onClose, onChange, users, spaces, lang, user }) {
    const [inv, setInv] = useState(null);
    const [comment, setComment] = useState("");
    const [commentSpokenAt, setCommentSpokenAt] = useState("");
    const [showSpokenAt, setShowSpokenAt] = useState(false);
    const [reassignOpen, setReassignOpen] = useState(false);
    const [deletionReason, setDeletionReason] = useState("");
    const [deletionDialogOpen, setDeletionDialogOpen] = useState(false);

    const isPriv = user?.role === "admin" || user?.role === "master";
    const canModifyAdmin = user?.role === "admin" || (user?.role === "master" && inv?.created_by_role !== "admin");

    const load = useCallback(async () => {
        if (!invId) return;
        try {
            const { data } = await api.get(`/invoices/${invId}`);
            setInv(data);
        } catch (e) {
            const s = e?.response?.status;
            if (s === 404 || s === 403) {
                toast.error(lang === "tr" ? "Erişim yok veya bulunamadı" : "Not found");
                onClose();
            }
        }
    }, [invId, onClose, lang]);

    useEffect(() => {
        if (open) load();
        else { setInv(null); setComment(""); }
    }, [open, load]);

    if (!open || !inv) return null;

    const updateField = async (patch) => {
        try {
            const { data } = await api.patch(`/invoices/${invId}`, patch);
            setInv((prev) => ({ ...prev, ...data }));
            onChange && onChange();
        } catch (e) {
            toast.error(errMsg(e, "Error"));
        }
    };

    const approve = async () => {
        try {
            const { data } = await api.post(`/invoices/${invId}/approve`);
            setInv((prev) => ({ ...prev, ...data }));
            onChange && onChange();
            toast.success(t(lang, "approve"));
        } catch (e) { toast.error(errMsg(e, "Error")); }
    };

    const markPaid = async () => {
        try {
            const { data } = await api.post(`/invoices/${invId}/mark-paid`);
            setInv((prev) => ({ ...prev, ...data }));
            onChange && onChange();
            toast.success(t(lang, "paid"));
        } catch (e) { toast.error(errMsg(e, "Error")); }
    };

    const uploadFileObject = async (file) => {
        if (!file) return;
        const form = new FormData();
        form.append("file", file);
        try {
            await api.post(`/invoices/${invId}/files`, form, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(t(lang, "uploadFile"));
            load();
        } catch (err) { toast.error(errMsg(err, "Upload failed")); }
    };
    const uploadFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        await uploadFileObject(file);
        e.target.value = "";
    };

    const addComment = async () => {
        if (!comment.trim()) return;
        try {
            await api.post(`/invoices/${invId}/comments`, {
                text: comment,
                mentioned_user_ids: [],
                spoken_at: commentSpokenAt ? new Date(commentSpokenAt).toISOString() : null,
            });
            setComment("");
            setCommentSpokenAt("");
            setShowSpokenAt(false);
            load();
        } catch (e) { toast.error(errMsg(e, "Error")); }
    };

    const editComment = async (commentId, text, spokenAt) => {
        try {
            await api.patch(`/invoices/${invId}/comments/${commentId}`, {
                text, spoken_at: spokenAt || null,
            });
            load();
        } catch (e) {
            toast.error(errMsg(e, "Error"));
        }
    };

    const del = async () => {
        if (!window.confirm(lang === "tr" ? "Silmek istediğinize emin misiniz?" : "Delete this?")) return;
        try {
            await api.delete(`/invoices/${invId}`);
            toast.success(t(lang, "delete"));
            onChange && onChange();
            onClose();
        } catch (e) { toast.error(errMsg(e, "Error")); }
    };

    const requestDeletion = async () => {
        try {
            await api.post(`/invoices/${invId}/request-deletion`, { reason: deletionReason || "" });
            toast.success(lang === "de" ? "Löschantrag gesendet" : "Deletion request sent");
            setDeletionDialogOpen(false);
            setDeletionReason("");
            load();
            onChange && onChange();
        } catch (e) { toast.error(errMsg(e, "Error")); }
    };

    const cancelDeletionRequest = async () => {
        try {
            await api.post(`/invoices/${invId}/cancel-deletion-request`);
            toast.success(lang === "de" ? "Löschantrag zurückgezogen" : "Request cancelled");
            load();
            onChange && onChange();
        } catch (e) { toast.error(errMsg(e, "Error")); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-3xl w-[95vw] max-h-[92vh] overflow-hidden p-0 flex flex-col" data-testid="invoice-detail-dialog">
                <DialogHeader className="px-5 sm:px-6 py-4 border-b border-neutral-200 text-left space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className={`uppercase tracking-wider text-[10px] font-bold ${
                            inv.status === "paid" ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
                            inv.status === "in_progress" ? "bg-amber-100 text-amber-800 border-amber-200" :
                            inv.status === "approved" ? "bg-blue-100 text-blue-800 border-blue-200" :
                            "bg-slate-100 text-slate-700 border-slate-200"
                        }`} data-testid={`inv-status-${inv.status}`}>
                            {t(lang, inv.status === "in_progress" ? "inProgress" : inv.status)}
                        </Badge>
                        <span className="text-[10px] uppercase tracking-[0.15em] text-neutral-500">{inv.invoice_number || "—"} · {inv.category || "—"}</span>
                    </div>
                    <DialogTitle className="font-display text-2xl font-bold tracking-tight">{inv.title}</DialogTitle>
                    <DialogDescription className="text-[11px] text-neutral-500">
                        {t(lang, "createdBy")}: <span className="font-medium text-neutral-700">{inv.created_by_name}</span> · {new Date(inv.created_at).toLocaleString(LOCALES[lang])}
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-4">
                    {inv.deletion_requested && (
                        <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 flex items-start gap-3" data-testid="inv-deletion-request-banner">
                            <AlertTriangle className="w-5 h-5 text-red-700 mt-0.5 shrink-0" />
                            <div className="flex-1 text-sm">
                                <p className="font-semibold text-red-900">{lang === "de" ? "Löschantrag offen" : "Deletion requested"}</p>
                                <p className="text-red-800 text-xs mt-0.5">
                                    <strong>{inv.deletion_requested_by_name}</strong> · {new Date(inv.deletion_requested_at).toLocaleString(LOCALES[lang])}
                                    {inv.deletion_reason ? <> — &bdquo;{inv.deletion_reason}&ldquo;</> : null}
                                </p>
                                {isPriv && canModifyAdmin ? (
                                    <div className="flex gap-2 mt-2.5">
                                        <Button size="sm" variant="destructive" onClick={del} data-testid="inv-approve-deletion">
                                            <Trash2 className="w-3.5 h-3.5 mr-1" />{lang === "de" ? "Löschen bestätigen" : "Approve delete"}
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={cancelDeletionRequest} data-testid="inv-reject-deletion">
                                            <XIcon className="w-3.5 h-3.5 mr-1" />{lang === "de" ? "Antrag ablehnen" : "Reject"}
                                        </Button>
                                    </div>
                                ) : (
                                    <Button size="sm" variant="outline" onClick={cancelDeletionRequest} className="mt-2" data-testid="inv-cancel-deletion">
                                        {lang === "de" ? "Antrag zurückziehen" : "Cancel request"}
                                    </Button>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <Field label={t(lang, "amount")} value={fmtMoney(inv.amount, inv.currency)} mono />
                        <Field label={t(lang, "dueDate")} value={inv.due_date ? new Date(inv.due_date).toLocaleDateString(LOCALES[lang]) : "—"} />
                        <Field label={t(lang, "urgency")} value={inv.urgency} />
                        <Field label={t(lang, "recurring")} value={inv.recurring || "—"} />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <Field label={t(lang, "vendor")} value={inv.vendor_name || "—"} />
                        <Field label={t(lang, "iban")} value={inv.iban || "—"} mono />
                    </div>
                    {inv.description && (
                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500 mb-1">{t(lang, "description")}</p>
                            <p className="text-sm whitespace-pre-wrap">{inv.description}</p>
                        </div>
                    )}

                    {isPriv && (
                        <div className="flex flex-wrap gap-2 pt-2">
                            {inv.status === "open" && canModifyAdmin && (
                                <Button size="sm" onClick={approve} data-testid="inv-approve-btn" className="bg-blue-600 hover:bg-blue-700">
                                    <CheckSquare className="w-3.5 h-3.5 mr-1" />{t(lang, "approve")}
                                </Button>
                            )}
                            {inv.status !== "paid" && canModifyAdmin && (
                                <Button size="sm" onClick={markPaid} data-testid="inv-mark-paid-btn" className="bg-brand-500 hover:bg-brand-600">
                                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" />{t(lang, "markPaid")}
                                </Button>
                            )}
                            {canModifyAdmin && (
                                <Select value={inv.status} onValueChange={(v) => updateField({ status: v })}>
                                    <SelectTrigger className="h-9 w-40" data-testid="inv-status-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="open">{t(lang, "open")}</SelectItem>
                                        <SelectItem value="approved">{t(lang, "approved")}</SelectItem>
                                        <SelectItem value="in_progress">{t(lang, "inProgress")}</SelectItem>
                                        <SelectItem value="paid">{t(lang, "paid")}</SelectItem>
                                    </SelectContent>
                                </Select>
                            )}
                            {canModifyAdmin && (
                                <Button size="sm" variant="outline" onClick={() => setReassignOpen(true)} data-testid="inv-reassign-btn">
                                    <Edit3 className="w-3.5 h-3.5 mr-1" />{lang === "de" ? "Zuweisung" : "Reassign"}
                                </Button>
                            )}
                            {canModifyAdmin && (
                                <Button size="sm" variant="destructive" onClick={del} data-testid="inv-delete-btn">
                                    <Trash2 className="w-3.5 h-3.5 mr-1" />{t(lang, "delete")}
                                </Button>
                            )}
                        </div>
                    )}

                    {!isPriv && !inv.deletion_requested && (
                        <div className="flex flex-wrap gap-2 pt-2">
                            <Button size="sm" variant="outline" onClick={() => setReassignOpen(true)} data-testid="inv-reassign-user-btn">
                                <Edit3 className="w-3.5 h-3.5 mr-1" />{lang === "de" ? "Zuweisung ändern" : "Reassign"}
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setDeletionDialogOpen(true)} data-testid="inv-request-deletion-btn" className="text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50">
                                <Trash2 className="w-3.5 h-3.5 mr-1" />{lang === "de" ? "Löschen anfordern" : "Request deletion"}
                            </Button>
                        </div>
                    )}

                    <Separator />

                    {/* Attachments */}
                    <section>
                        <div className="flex items-center justify-between mb-2">
                            <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500">{t(lang, "attachments")} ({(inv.attachments || []).length})</h4>
                            <label className="text-xs flex items-center gap-1 px-2 py-1 rounded-md border border-neutral-300 hover:bg-neutral-50 cursor-pointer">
                                <Paperclip className="w-3 h-3" />{t(lang, "uploadFile")}
                                <input type="file" hidden onChange={uploadFile} data-testid="inv-upload" />
                            </label>
                        </div>
                        <DropZone
                            accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx"
                            onFile={uploadFileObject}
                            compact
                            label={lang === "de" ? "Datei hierher ziehen, Str+V einfügen oder klicken" : "Drop file here, paste with Ctrl+V, or click"}
                            testId="inv-detail-dropzone"
                            className="mb-2"
                        />
                        <div className="space-y-1.5">
                            {(inv.attachments || []).map((f) => (
                                <div key={f.id} className="flex items-center gap-2 p-2 rounded-md border border-neutral-200 bg-neutral-50/50">
                                    <FileText className="w-3.5 h-3.5 text-neutral-500" />
                                    <span className="flex-1 text-sm truncate">{f.original_filename}</span>
                                    <span className="text-[11px] text-neutral-500">{(f.size / 1024).toFixed(1)} KB</span>
                                </div>
                            ))}
                        </div>
                    </section>

                    <Separator />

                    {/* Activity */}
                    <section>
                        <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500 mb-2">{t(lang, "activity")}</h4>
                        <ScrollArea className="max-h-32 border border-neutral-200 rounded-md bg-white p-2" data-testid="inv-activity">
                            <ul className="space-y-1.5">
                                {(inv.activity || []).slice().reverse().map((a) => (
                                    <li key={a.id} className="text-[11px] text-neutral-600">
                                        <span className="font-semibold text-neutral-800">{a.user_name}</span>{" "}<span>{a.action}</span>
                                        {a.details && <span className="text-neutral-500"> — {a.details}</span>}
                                        <span className="block text-neutral-400 text-[10px]">{new Date(a.timestamp).toLocaleString(LOCALES[lang])}</span>
                                    </li>
                                ))}
                            </ul>
                        </ScrollArea>
                    </section>

                    <Separator />

                    {/* Comments */}
                    <section>
                        <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500 mb-2">{t(lang, "comments")} ({(inv.comments || []).length})</h4>
                        <div className="space-y-2 mb-2">
                            {(inv.comments || []).map((c) => (
                                <InvCommentItem
                                    key={c.id}
                                    c={c}
                                    lang={lang}
                                    canEdit={c.user_id === user?.id || user?.role === "admin" || user?.role === "master"}
                                    onSave={(text, spokenAt) => editComment(c.id, text, spokenAt)}
                                />
                            ))}
                        </div>
                        <div className="space-y-1.5">
                            <div className="flex gap-2 items-stretch">
                                <Input value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t(lang, "addComment")} data-testid="inv-comment-input"
                                    onKeyDown={(e) => e.key === "Enter" && addComment()} />
                                <Button onClick={addComment} disabled={!comment.trim()} className="bg-neutral-900 hover:bg-neutral-800" data-testid="inv-comment-send"><Send className="w-4 h-4" /></Button>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                {!showSpokenAt && !commentSpokenAt ? (
                                    <button
                                        type="button"
                                        onClick={() => setShowSpokenAt(true)}
                                        data-testid="inv-add-spoken-at-btn"
                                        className="text-[11px] flex items-center gap-1 px-2 py-1 rounded-md border border-dashed border-neutral-300 text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900"
                                    >
                                        <CalendarIcon className="w-3 h-3" /> {t(lang, "addSpokenAt")}
                                    </button>
                                ) : (
                                    <div className="flex items-center gap-1.5">
                                        <CalendarIcon className="w-3 h-3 text-neutral-500" />
                                        <Input
                                            type="date"
                                            data-testid="inv-comment-spoken-at-input"
                                            value={commentSpokenAt}
                                            onChange={(e) => setCommentSpokenAt(e.target.value)}
                                            className="h-8 w-[155px] text-xs"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => { setCommentSpokenAt(""); setShowSpokenAt(false); }}
                                            className="text-neutral-500 hover:text-red-600 text-[10px] underline"
                                        >
                                            {t(lang, "cancel")}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </section>
                </div>
            </DialogContent>
            <ReassignInvoiceDialog open={reassignOpen} onClose={() => setReassignOpen(false)} inv={inv} users={users} spaces={spaces} onSaved={() => { setReassignOpen(false); load(); onChange && onChange(); }} lang={lang} />
            <RequestDeletionDialog open={deletionDialogOpen} onClose={() => setDeletionDialogOpen(false)} reason={deletionReason} setReason={setDeletionReason} onConfirm={requestDeletion} lang={lang} />
        </Dialog>
    );
}

function ReassignInvoiceDialog({ open, onClose, inv, users, spaces, onSaved, lang }) {
    const [assignees, setAssignees] = useState([]);
    const [spaceIds, setSpaceIds] = useState([]);
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        if (open && inv) {
            setAssignees(inv.assignee_ids || []);
            setSpaceIds(inv.space_ids || []);
        }
    }, [open, inv]);
    const submit = async () => {
        setBusy(true);
        try {
            await api.patch(`/invoices/${inv.id}`, { assignee_ids: assignees, space_ids: spaceIds });
            toast.success(t(lang, "save"));
            onSaved();
        } catch (e) { toast.error(errMsg(e, "Error")); } finally { setBusy(false); }
    };
    if (!inv) return null;
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md" data-testid="reassign-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">{lang === "de" ? "Zuweisung ändern" : "Reassign"}</DialogTitle>
                    <DialogDescription className="text-xs">{inv.title}</DialogDescription>
                </DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{lang === "de" ? "Personen" : "People"}</Label>
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {users.map((u) => {
                                const sel = assignees.includes(u.id);
                                return (
                                    <button key={u.id} type="button" data-testid={`reassign-user-${u.id}`}
                                        onClick={() => setAssignees((p) => p.includes(u.id) ? p.filter((x) => x !== u.id) : [...p, u.id])}
                                        className={`px-2 py-1 rounded-md text-xs border ${sel ? "bg-neutral-900 text-white border-neutral-900" : "border-neutral-300 hover:bg-neutral-100"}`}>
                                        {u.name}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider">{lang === "de" ? "Abteilungen" : "Departments"}</Label>
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                            {(spaces || []).map((s) => {
                                const sel = spaceIds.includes(s.id);
                                return (
                                    <button key={s.id} type="button" data-testid={`reassign-space-${s.id}`}
                                        onClick={() => setSpaceIds((p) => p.includes(s.id) ? p.filter((x) => x !== s.id) : [...p, s.id])}
                                        className={`px-2 py-1 rounded-md text-xs border ${sel ? "bg-neutral-900 text-white border-neutral-900" : "border-neutral-300 hover:bg-neutral-100"}`}
                                        style={sel ? {} : { borderLeft: `3px solid ${s.color || "#0a0a0a"}` }}>
                                        {s.name}
                                    </button>
                                );
                            })}
                            {(!spaces || spaces.length === 0) && <p className="text-xs text-neutral-500 italic">{lang === "de" ? "Keine Abteilungen vorhanden" : "No departments"}</p>}
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{t(lang, "cancel")}</Button>
                    <Button onClick={submit} disabled={busy} data-testid="reassign-save" className="bg-neutral-900 hover:bg-neutral-800">{t(lang, "save")}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function RequestDeletionDialog({ open, onClose, reason, setReason, onConfirm, lang }) {
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-md" data-testid="request-deletion-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">{lang === "de" ? "Löschen anfordern" : "Request deletion"}</DialogTitle>
                    <DialogDescription className="text-xs">
                        {lang === "de" ? "Admin oder Master müssen den Löschvorgang bestätigen. Bitte kurze Begründung angeben." : "An admin or master needs to approve. Please give a short reason."}
                    </DialogDescription>
                </DialogHeader>
                <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider">{lang === "de" ? "Begründung" : "Reason"}</Label>
                    <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3} data-testid="request-deletion-reason" className="mt-1.5"
                              placeholder={lang === "de" ? "z.B. Duplikat, Falscheingabe..." : "e.g. duplicate, wrong entry..."} />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>{t(lang, "cancel")}</Button>
                    <Button onClick={onConfirm} className="bg-red-600 hover:bg-red-700" data-testid="request-deletion-confirm">
                        <Send className="w-3.5 h-3.5 mr-1" />{lang === "de" ? "Antrag senden" : "Send request"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, value, mono }) {
    return (
        <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-500">{label}</p>
            <p className={`text-sm font-medium ${mono ? "font-mono" : ""}`}>{value}</p>
        </div>
    );
}

function InvCommentItem({ c, lang, canEdit, onSave }) {
    const [editing, setEditing] = useState(false);
    const [text, setText] = useState(c.text);
    const [spokenAt, setSpokenAt] = useState(c.spoken_at ? c.spoken_at.slice(0, 10) : "");

    useEffect(() => {
        setText(c.text);
        setSpokenAt(c.spoken_at ? c.spoken_at.slice(0, 10) : "");
    }, [c.id, c.text, c.spoken_at]);

    const save = () => {
        onSave(text, spokenAt ? new Date(spokenAt).toISOString() : null);
        setEditing(false);
    };

    return (
        <div className="flex gap-2 text-sm" data-testid={`inv-comment-${c.id}`}>
            <div className="w-6 h-6 rounded-full bg-neutral-900 text-white text-[9px] font-semibold flex items-center justify-center shrink-0">
                {c.user_name?.slice(0, 2)?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-xs">
                    <span className="font-semibold">{c.user_name}</span>{" "}
                    <span className="text-neutral-500 text-[10px]">{new Date(c.timestamp).toLocaleString(LOCALES[lang])}</span>
                    {c.edited_at && <span className="text-neutral-400 ml-1 italic text-[10px]">· {lang === "de" ? "bearb." : lang === "tr" ? "düz." : "edited"}</span>}
                    {canEdit && !editing && (
                        <button
                            onClick={() => setEditing(true)}
                            data-testid={`inv-edit-comment-${c.id}`}
                            className="ml-2 text-neutral-500 hover:text-neutral-900 underline text-[10px]"
                        >
                            <Edit3 className="w-2.5 h-2.5 inline" /> {t(lang, "edit")}
                        </button>
                    )}
                </p>
                {c.spoken_at && !editing && (
                    <p className="text-[11px] text-amber-800 bg-amber-50 inline-flex items-center gap-1 mt-0.5 px-1.5 py-0.5 rounded border border-amber-200" data-testid={`inv-spoken-at-${c.id}`}>
                        <CalendarIcon className="w-2.5 h-2.5" />
                        <span className="font-semibold">{t(lang, "spokenOn")}</span>
                        <span>{new Date(c.spoken_at).toLocaleDateString(LOCALES[lang])}</span>
                    </p>
                )}
                {editing ? (
                    <div className="space-y-1.5 mt-1">
                        <Textarea
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            rows={2}
                            data-testid={`inv-edit-comment-text-${c.id}`}
                            className="text-sm"
                        />
                        <div className="flex items-center gap-2 flex-wrap">
                            <div className="flex items-center gap-1.5">
                                <CalendarIcon className="w-3 h-3 text-neutral-500" />
                                <Input
                                    type="date"
                                    value={spokenAt}
                                    onChange={(e) => setSpokenAt(e.target.value)}
                                    data-testid={`inv-edit-comment-spoken-${c.id}`}
                                    className="h-7 w-[150px] text-xs"
                                />
                                {spokenAt && (
                                    <button
                                        type="button"
                                        onClick={() => setSpokenAt("")}
                                        className="text-[10px] text-neutral-500 hover:text-red-600 underline"
                                    >×</button>
                                )}
                            </div>
                            <div className="flex gap-1">
                                <Button size="sm" onClick={save} data-testid={`inv-save-comment-${c.id}`} className="h-7 px-2 text-xs bg-neutral-900 hover:bg-neutral-800">
                                    <Save className="w-3 h-3 mr-1" />{t(lang, "save")}
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setText(c.text); setSpokenAt(c.spoken_at ? c.spoken_at.slice(0, 10) : ""); }} className="h-7 px-2 text-xs">
                                    {t(lang, "cancel")}
                                </Button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <p className="text-sm whitespace-pre-wrap break-words">{c.text}</p>
                )}
            </div>
        </div>
    );
}

const MONTH_NAMES = {
    de: ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"],
    en: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
    tr: ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"],
};

function MonthlyChartDialog({ open, onClose, lang }) {
    const today = new Date();
    const [year, setYear] = useState(today.getFullYear());
    const [month, setMonth] = useState(today.getMonth() + 1); // 1-12
    const [data, setData] = useState({ days: [] });
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/invoices/monthly-breakdown?year=${year}&month=${month}`);
            setData(data);
        } catch (e) {
            toast.error(errMsg(e, "Error"));
        } finally {
            setLoading(false);
        }
    }, [year, month]);

    useEffect(() => {
        if (open) load();
    }, [open, load]);

    const daysInMonth = new Date(year, month, 0).getDate();
    const dayMap = React.useMemo(() => {
        const m = {};
        (data.days || []).forEach((d) => { m[d.day] = d; });
        return m;
    }, [data]);

    const maxAmount = React.useMemo(() => {
        let max = 0;
        (data.days || []).forEach((d) => { if (d.total > max) max = d.total; });
        return max || 1;
    }, [data]);

    const totalMonth = React.useMemo(() => {
        return (data.days || []).reduce((s, d) => s + (d.total || 0), 0);
    }, [data]);
    const totalPaid = React.useMemo(() => {
        return (data.days || []).reduce((s, d) => s + (d.paid || 0), 0);
    }, [data]);
    const totalOpen = totalMonth - totalPaid;

    const navMonth = (delta) => {
        let m = month + delta;
        let y = year;
        if (m < 1) { m = 12; y -= 1; }
        if (m > 12) { m = 1; y += 1; }
        setMonth(m); setYear(y);
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-4xl w-[95vw] max-h-[92vh] overflow-hidden p-0 flex flex-col" data-testid="monthly-chart-dialog">
                <DialogHeader className="px-5 sm:px-6 py-4 border-b border-neutral-200 text-left">
                    <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2">
                        <BarChart3 className="w-5 h-5" /> {t(lang, "monthlyChart")}
                    </DialogTitle>
                    <DialogDescription className="text-xs text-neutral-500">{t(lang, "expensesByDay")}</DialogDescription>
                </DialogHeader>

                <div className="px-5 sm:px-6 py-3 border-b border-neutral-200 flex flex-wrap items-center gap-3 bg-neutral-50/50">
                    <button onClick={() => navMonth(-1)} data-testid="chart-prev-month" className="w-8 h-8 rounded-md border border-neutral-300 hover:bg-white flex items-center justify-center">
                        <ChevronLeft className="w-4 h-4" />
                    </button>
                    <div className="flex items-center gap-2">
                        <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v, 10))}>
                            <SelectTrigger className="h-9 w-[140px]" data-testid="chart-month-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {(MONTH_NAMES[lang] || MONTH_NAMES.en).map((n, i) => (
                                    <SelectItem key={i} value={String(i + 1)}>{n}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v, 10))}>
                            <SelectTrigger className="h-9 w-[100px]" data-testid="chart-year-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {[year - 2, year - 1, year, year + 1, year + 2].map((y) => (
                                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <button onClick={() => navMonth(1)} data-testid="chart-next-month" className="w-8 h-8 rounded-md border border-neutral-300 hover:bg-white flex items-center justify-center">
                        <ChevronRight className="w-4 h-4" />
                    </button>

                    <div className="ml-auto flex flex-wrap items-center gap-3 text-xs">
                        <div className="flex items-center gap-1.5">
                            <span className="w-3 h-3 rounded bg-emerald-500" />
                            <span className="text-neutral-700">{t(lang, "paid")}: <span className="font-mono font-semibold">{fmtMoney(totalPaid, "EUR")}</span></span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <span className="w-3 h-3 rounded bg-amber-500" />
                            <span className="text-neutral-700">{t(lang, "open")}: <span className="font-mono font-semibold">{fmtMoney(totalOpen, "EUR")}</span></span>
                        </div>
                        <div className="border-l border-neutral-300 pl-3">
                            <span className="text-neutral-500 text-[10px] uppercase tracking-wider mr-1">{lang === "de" ? "Gesamt" : lang === "tr" ? "Toplam" : "Total"}</span>
                            <span className="font-mono font-bold text-base">{fmtMoney(totalMonth, "EUR")}</span>
                        </div>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-5 sm:p-6">
                    {loading ? (
                        <div className="flex items-center justify-center h-64">
                            <Loader2 className="w-6 h-6 animate-spin text-neutral-500" />
                        </div>
                    ) : totalMonth === 0 ? (
                        <div className="flex flex-col items-center justify-center h-64 text-neutral-400">
                            <BarChart3 className="w-12 h-12 mb-2 opacity-30" />
                            <p className="text-sm">{t(lang, "noExpenses")}</p>
                        </div>
                    ) : (
                        <div data-testid="chart-bars">
                            {/* Chart area */}
                            <div className="relative h-72 border-l-2 border-b-2 border-neutral-300 bg-white ml-20">
                                {/* Y axis grid lines */}
                                {[0.25, 0.5, 0.75, 1].map((p) => (
                                    <div key={p} className="absolute left-0 right-0 border-t border-dashed border-neutral-200" style={{ bottom: `${p * 100}%` }}>
                                        <span className="absolute -left-20 -top-2 text-[10px] text-neutral-500 font-mono w-16 text-right">
                                            {fmtMoney(maxAmount * p, "EUR")}
                                        </span>
                                    </div>
                                ))}
                                {/* Bars */}
                                <div className="absolute inset-0 flex items-stretch gap-px px-1">
                                    {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
                                        const d = dayMap[day];
                                        const total = d?.total || 0;
                                        const paid = d?.paid || 0;
                                        const openAmt = d?.open || 0;
                                        const heightPct = (total / maxAmount) * 100;
                                        const paidPct = total > 0 ? (paid / total) * 100 : 0;
                                        return (
                                            <div
                                                key={day}
                                                className="flex-1 flex flex-col items-center justify-end h-full group relative"
                                                data-testid={`chart-bar-day-${day}`}
                                                title={total > 0 ? `${day}: ${fmtMoney(total, "EUR")}` : ""}
                                            >
                                                {total > 0 && (
                                                    <>
                                                        <div
                                                            className="w-full max-w-[20px] rounded-t flex flex-col-reverse overflow-hidden shadow-sm transition-all hover:opacity-90"
                                                            style={{ height: `${heightPct}%`, minHeight: "3px" }}
                                                        >
                                                            <div className="bg-emerald-500" style={{ height: `${paidPct}%` }} />
                                                            <div className="bg-amber-500" style={{ height: `${100 - paidPct}%` }} />
                                                        </div>
                                                        {/* Tooltip */}
                                                        <div className="absolute -top-14 left-1/2 -translate-x-1/2 hidden group-hover:block z-10 bg-neutral-900 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap pointer-events-none">
                                                            <p className="font-semibold">{day}. {(MONTH_NAMES[lang] || MONTH_NAMES.en)[month - 1]}</p>
                                                            <p>{lang === "de" ? "Gesamt" : "Total"}: <span className="font-mono font-bold">{fmtMoney(total, "EUR")}</span></p>
                                                            {paid > 0 && <p className="text-emerald-300">{t(lang, "paid")}: {fmtMoney(paid, "EUR")}</p>}
                                                            {openAmt > 0 && <p className="text-amber-300">{t(lang, "open")}: {fmtMoney(openAmt, "EUR")}</p>}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                            {/* X axis: day numbers */}
                            <div className="flex gap-px px-1 mt-1 ml-20">
                                {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => (
                                    <div
                                        key={day}
                                        className={`flex-1 text-center text-[9px] font-mono ${dayMap[day]?.total > 0 ? "text-neutral-900 font-bold" : "text-neutral-400"}`}
                                    >
                                        {day % 5 === 0 || day === 1 || day === daysInMonth ? day : "·"}
                                    </div>
                                ))}
                            </div>

                            {/* Day-by-day breakdown table */}
                            <div className="mt-6">
                                <h4 className="text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500 mb-2">{t(lang, "expensesByDay")}</h4>
                                <div className="border border-neutral-200 rounded-md overflow-hidden">
                                    <div className="overflow-x-auto">
                                    <table className="w-full text-sm min-w-[520px]" data-testid="chart-breakdown-table">
                                        <thead className="bg-neutral-50 text-[10px] uppercase tracking-wider text-neutral-500">
                                            <tr>
                                                <th className="text-left p-2 font-semibold">{lang === "de" ? "Tag" : lang === "tr" ? "Gün" : "Day"}</th>
                                                <th className="text-right p-2 font-semibold">{lang === "de" ? "Gesamt" : lang === "tr" ? "Toplam" : "Total"}</th>
                                                <th className="text-right p-2 font-semibold">{t(lang, "paid")}</th>
                                                <th className="text-right p-2 font-semibold">{t(lang, "open")}</th>
                                                <th className="text-left p-2 font-semibold">{lang === "de" ? "Rechnungen" : lang === "tr" ? "Faturalar" : "Invoices"}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(data.days || []).slice().sort((a, b) => a.day - b.day).map((d) => (
                                                <tr key={d.day} className="border-t border-neutral-100 hover:bg-neutral-50" data-testid={`chart-row-day-${d.day}`}>
                                                    <td className="p-2 font-semibold">{d.day}.</td>
                                                    <td className="p-2 text-right font-mono">{fmtMoney(d.total, "EUR")}</td>
                                                    <td className="p-2 text-right font-mono text-emerald-700">{d.paid > 0 ? fmtMoney(d.paid, "EUR") : "—"}</td>
                                                    <td className="p-2 text-right font-mono text-amber-700">{d.open > 0 ? fmtMoney(d.open, "EUR") : "—"}</td>
                                                    <td className="p-2 text-xs text-neutral-600 truncate max-w-[300px]">
                                                        {d.items.map((i) => i.title).join(", ")}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
